import { isLeapYear } from './leap.js';
import type { Calendar, MonthDefinition, MonthsCycle, WeekdaysCycle } from './types.js';

/**
 * Accessors for the runtime calendar's cycles. A runtime `Calendar` always
 * carries the **legacy** `{ year, week? }` cycles arm — a bag-form
 * *definition* is projected INTO this arm at narrowing time
 * (`legacyCyclesFromBag` in `calendar-definition.ts`, A2 #231), so the
 * runtime type stays narrow and every existing consumer keeps reading
 * `cycles.year` / `cycles.week`. These helpers remain the single read-site
 * the leap-aware `DisplayDate` walk goes through.
 *
 * The *general* multi-cycle runtime (`cyclePosition` / `cycleLabel` in
 * `cycles.ts`) operates on the bag form (via `normalizeCycles`), so atomic
 * and nested resolution is available regardless of which arm a calendar
 * carries — that is what `convert.ts` drives the weekday projection off.
 */

/** The calendar's months cycle. */
export function monthsCycle(calendar: Calendar): MonthsCycle {
  return calendar.cycles.year;
}

/** The calendar's optional weekdays cycle. */
export function weekdaysCycle(calendar: Calendar): WeekdaysCycle | undefined {
  return calendar.cycles.week;
}

/**
 * Year-mechanics helpers. All functions here operate on display years
 * (era-relative integers); the era-aware arithmetic lives in `convert.ts`.
 *
 * These helpers are pure and allocation-free on the hot path: the
 * conversion loop iterates one year at a time and asks for the year's
 * total length plus the cumulative day-count of months within that year.
 * For 10k-iteration property tests the iteration depth is bounded by the
 * year range chosen by the test (max ~10k years), which is acceptable
 * since each step is constant time.
 */

/** Length of `month_index` (1-based) in `year`. */
export function monthLength(calendar: Calendar, year: number, monthIndex: number): number {
  const m = monthsCycle(calendar).months[monthIndex - 1];
  if (m === undefined) throw new Error(`month index ${monthIndex} out of range`);
  if (m.leap_length !== undefined && isLeapYear(calendar.leap_rule, year)) {
    return m.leap_length;
  }
  return m.length;
}

/** Total length of `year` (sum of month lengths under the year's leap status). */
export function yearLength(calendar: Calendar, year: number): number {
  let total = 0;
  for (const m of monthsCycle(calendar).months) {
    total += monthLengthForYear(m, calendar.leap_rule, year);
  }
  return total;
}

function monthLengthForYear(m: MonthDefinition, rule: Calendar['leap_rule'], year: number): number {
  if (m.leap_length !== undefined && isLeapYear(rule, year)) return m.leap_length;
  return m.length;
}

/**
 * Number of days from `(year, 1, 1)` to `(year, monthIndex, day)`, with
 * `day` 1-indexed. Caller is responsible for validating that `day` is
 * within the month's length.
 */
export function dayOfYear(
  calendar: Calendar,
  year: number,
  monthIndex: number,
  day: number,
): number {
  let total = 0;
  for (let i = 1; i < monthIndex; i++) {
    total += monthLength(calendar, year, i);
  }
  return total + (day - 1);
}

/**
 * Inverse of `dayOfYear`: given a 0-indexed day-of-year, return the
 * `(monthIndex, day)` pair within `year`. Throws if `doy` is outside
 * `[0, yearLength(year))`.
 */
export function monthAndDayFromDayOfYear(
  calendar: Calendar,
  year: number,
  doy: number,
): { monthIndex: number; day: number } {
  let remaining = doy;
  for (let i = 1; i <= monthsCycle(calendar).months.length; i++) {
    const len = monthLength(calendar, year, i);
    if (remaining < len) {
      return { monthIndex: i, day: remaining + 1 };
    }
    remaining -= len;
  }
  throw new Error(`day-of-year ${doy} exceeds year ${year} length`);
}
